import React, { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IntegrationsManager = () => {
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState(null);

  useEffect(() => {
    loadIntegrationStatus();
  }, []);

  const loadIntegrationStatus = async () => {
    try {
      const response = await axios.get(`${API}/integrations/`);
      setIntegrations(response.data.integrations);
    } catch (error) {
      console.error("Erreur lors du chargement des intégrations:", error);
    } finally {
      setLoading(false);
    }
  };

  const syncIcal = async () => {
    setSyncing(true);
    setSyncResults(null);
    
    try {
      const response = await axios.post(`${API}/ical/sync-all`);
      setSyncResults({
        status: "success",
        message: "Synchronisation iCal terminée",
        details: response.data
      });
    } catch (error) {
      setSyncResults({
        status: "error",
        message: error.response?.data?.detail || "Erreur lors de la synchronisation iCal"
      });
    } finally {
      setSyncing(false);
    }
  };

  const getStatusIcon = (integration) => {
    if (integration?.available && integration?.configured) {
      return "✅";
    } else if (integration?.available) {
      return "⚠️";
    } else {
      return "❌";
    }
  };

  const getStatusText = (integration) => {
    if (integration?.available && integration?.configured) {
      return "Configuré et actif";
    } else if (integration?.available) {
      return "Disponible";
    } else {
      return "Non disponible";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Chargement des intégrations...</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4" data-testid="integrations-manager">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">🔗 Intégrations</h1>
        <p className="text-gray-600 mt-2">
          Gérez les connexions avec les services externes
        </p>
      </div>

      {/* Résultats de synchronisation */}
      {syncResults && (
        <div className={`mb-6 p-4 rounded-md ${
          syncResults.status === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          <div className={`font-medium ${
            syncResults.status === 'success' ? 'text-green-800' : 'text-red-800'
          }`}>
            {syncResults.status === 'success' ? '✅' : '❌'} {syncResults.message}
          </div>
          {syncResults.details && (
            <div className="mt-2 text-sm text-gray-600">
              {syncResults.details.synced_housings !== undefined && (
                <p>Logements synchronisés : {syncResults.details.synced_housings}</p>
              )}
              {syncResults.details.new_reservations !== undefined && (
                <p>Nouvelles réservations : {syncResults.details.new_reservations}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* iCal Integration */}
      <div className="card mb-6">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2 text-2xl">📅</span>
              iCal (Airbnb, Booking, etc.)
            </h2>
            <span className="badge badge-success">
              Actif
            </span>
          </div>
        </div>
        <div className="card-body">
          <p className="text-gray-600 mb-4">
            Import automatique des réservations depuis vos calendriers Airbnb, Booking, Abritel et autres plateformes.
          </p>
          
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-md mb-4">
            <p className="text-sm text-blue-800">
              <strong>ℹ️ Synchronisation automatique</strong><br/>
              Les calendriers iCal sont synchronisés automatiquement toutes les 5 minutes.
              Les réservations sont importées et les missions de ménage sont créées automatiquement.
            </p>
          </div>
          
          <button
            onClick={syncIcal}
            disabled={syncing}
            className="btn-primary"
            data-testid="sync-ical-btn"
          >
            {syncing ? "🔄 Synchronisation..." : "🔄 Synchroniser maintenant"}
          </button>
        </div>
      </div>

      {/* Instructions */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">📚 Comment ajouter un calendrier iCal</h2>
        </div>
        <div className="card-body">
          <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
            <li>Allez dans la fiche d'un <strong>logement</strong></li>
            <li>Dans la section "Calendrier iCal", collez l'URL du calendrier</li>
            <li>Les réservations seront importées automatiquement</li>
          </ol>
          
          <div className="mt-4 p-3 bg-gray-50 rounded-md">
            <h4 className="font-medium text-gray-900 mb-2">Où trouver l'URL iCal ?</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li><strong>Airbnb :</strong> Calendrier → Exporter le calendrier → Copier le lien</li>
              <li><strong>Booking :</strong> Extranet → Calendrier → Synchroniser les calendriers</li>
              <li><strong>Abritel :</strong> Calendrier → Exporter → Copier l'URL iCal</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationsManager;
